"use client"

// src/components/pages/RegisterPage.jsx
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import DashboardTemplate from "../templates/DashboardTemplate"
import Input from "../atoms/Input"
import Button from "../atoms/Button"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"

export default function RegisterPage() {
  const { login: saveAuth } = useAuth()
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [generalError, setGeneralError] = useState(null)
  const navigate = useNavigate()

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    // Clear error for this field when user types
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: null,
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Username validation
    if (!form.username.trim()) {
      newErrors.username = "Username is required"
    } else if (form.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters"
    }

    // Email validation
    if (!form.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email is invalid"
    }

    // Password validation
    if (!form.password) {
      newErrors.password = "Password is required"
    } else if (form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters"
    }

    // Confirm password validation
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }

    // Phone validation
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    setGeneralError(null)

    try {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...submitData } = form
      const { data } = await api.post("/auth/register", submitData)
      saveAuth(data.user, data.token)
      navigate("/browse-restaurants")
    } catch (err) {
      console.error("Registration error:", err)
      setGeneralError(err.response?.data?.error || "Registration failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardTemplate>
      <div className="max-w-md mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-200">
          <h1 className="text-2xl font-bold mb-6 text-amber-800 border-b border-amber-200 pb-2">Create an Account</h1>

          {generalError && (
            <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              {generalError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-2 font-medium text-amber-700">Username</label>
              <Input
                name="username"
                placeholder="Choose a username"
                value={form.username}
                onChange={handleChange}
                className={`border-amber-300 focus:ring-amber-500 focus:border-amber-500 ${errors.username ? "border-red-500" : ""}`}
              />
              {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
            </div>

            <div>
              <label className="block mb-2 font-medium text-amber-700">Email</label>
              <Input
                name="email"
                type="email"
                placeholder="Your email address"
                value={form.email}
                onChange={handleChange}
                className={`border-amber-300 focus:ring-amber-500 focus:border-amber-500 ${errors.email ? "border-red-500" : ""}`}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block mb-2 font-medium text-amber-700">Password</label>
              <Input
                name="password"
                type="password"
                placeholder="Create a password"
                value={form.password}
                onChange={handleChange}
                className={`border-amber-300 focus:ring-amber-500 focus:border-amber-500 ${errors.password ? "border-red-500" : ""}`}
              />
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label className="block mb-2 font-medium text-amber-700">Confirm Password</label>
              <Input
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={form.confirmPassword}
                onChange={handleChange}
                className={`border-amber-300 focus:ring-amber-500 focus:border-amber-500 ${errors.confirmPassword ? "border-red-500" : ""}`}
              />
              {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
            </div>

            <div>
              <label className="block mb-2 font-medium text-amber-700">Phone Number</label>
              <Input
                name="phone"
                type="tel"
                placeholder="+1 (123) 456-7890"
                value={form.phone}
                onChange={handleChange}
                className={`border-amber-300 focus:ring-amber-500 focus:border-amber-500 ${errors.phone ? "border-red-500" : ""}`}
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white mt-6">
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </div>
              ) : (
                "Create Account"
              )}
            </Button>

            <div className="text-center mt-4 pt-4 border-t border-amber-200">
              <p className="text-gray-600">
                Already have an account?{" "}
                <a href="/login" className="text-amber-600 hover:text-amber-800 font-medium">
                  Log In
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </DashboardTemplate>
  )
}
